import React, { Fragment, useContext } from 'react'
import { ContentBlock } from 'components/ContentBlock'
import Divider from 'components/Divider'
import DataTable from 'components/DataTableTest'
import { DataTable as OldDataTable } from 'components/DataTable'
import {
	total_salesGroup,
	total_currency,
	total_credit,
	total_output,
	total_statistics,
	total_specialOps,
	total_rate,
	total_eftbreakdown
} from '../tableColumns'
import { Row, Col } from 'react-flexbox-grid'
import formatDate from 'helpers/dateTimeFormatter'
import { useTranslation } from 'react-i18next'
import useLocale from 'hooks/useLocale'
import get from 'lodash/get'
import { TextEditor } from 'components/forms'

import { useQuery, useMutation } from 'components/graphql'
import updateClosingMoney from '../gql/updateClosingMoney'
import getMainCurrency from '../gql/getMainCurrency'
import numberFormatter from 'helpers/numberFormatter'
import Button from 'components/Button'
import useClosingSum from '../hooks/useClosingSum'
import { RefreshSideListContext } from '../../../components/SectionHandler'

const Total = ({ data, listQueryRef }) => {
	const result = useQuery(getMainCurrency, { fetchPolicy: 'cache-only' })
	const mainCurrency = get(result, 'data.getAppConfig.mainCurrency')

	const { setRefreshSideList } = useContext(RefreshSideListContext)

	const [t] = useTranslation()
	const locale = useLocale()
	const [updatePresent] = useMutation(updateClosingMoney)
	// eslint-disable-next-line react-hooks/refs
	const { toggleSum, isSum, loading, data: sumData } = useClosingSum(
		{ variables: listQueryRef.current.variables }, // eslint-disable-line react-hooks/refs
		() => setRefreshSideList(true)
	)

	const currentData = isSum ? sumData.statistics : data
	const statistics = {
		[t('closing_section.strings.statistic_number_of_receipts')]: numberFormatter(currentData.clientCount, { locale, toFixed: 0 }),
		[t('closing_section.strings.statistic_product_count')]: numberFormatter(currentData.productCount, { locale, toFixed: 0 }),
		[t('closing_section.strings.statistic_revenue_per_receipt')]: currentData.clientCount ? numberFormatter(currentData.total / currentData.clientCount, { locale }) : null,
		[t('closing_section.strings.statistic_items_per_receipt')]: currentData.clientCount ? numberFormatter(currentData.productCount / currentData.clientCount, { locale }) : null,
		[t('closing_section.strings.statistic_total_per_product')]: currentData.productCount ? numberFormatter(currentData.total / currentData.productCount, { locale }) : null,
		[t('closing_section.strings.statistic_cancelations')]: numberFormatter(currentData.cancellationsCount, { locale, toFixed: 0 }),
		[t('common.strings.from')]: formatDate(get(currentData, 'dateInterval.from'), 'DD.MM.YYYY HH:mm:ss'),
		[t('common.strings.to')]: formatDate(get(currentData, 'dateInterval.to'), 'DD.MM.YYYY HH:mm:ss'),
		[t('closing_section.strings.statistic_from_bill')]: numberFormatter(currentData.billNumStart, { locale, toFixed: 0 }),
		[t('closing_section.strings.statistic_to_bill')]: numberFormatter(currentData.billNumEnd, { locale, toFixed: 0 }),
		[t('closing_section.strings.statistic_count_from')]: formatDate(currentData.moneyCountDateTime, 'DD.MM.YYYY HH:mm:ss'),
		SafeBag: currentData.doorCounter,
		...currentData.guestsCount && { [t('closing_section.strings.statistic_guests_count')]: numberFormatter(currentData.guestsCount, { locale, toFixed: 0 }) }
	}

	const updateData = ({ currency, newValue, field, reason }) => {
		let fields = {}

		if(field === 'cashInTransit') {
			fields.cashInTransit = newValue
		}

		if(field === 'present') {
			switch (currency) {
				case mainCurrency:
					fields.chf_present = newValue
					break
				case 'EUR':
					fields.euro_present = newValue
					break
				default:
					const index = data.currency.findIndex(item => item.name === currency)
					fields.currencyPresent = []
					fields.currencyPresent[index] = newValue
			}
		}
		updatePresent({
			variables: {
				id: data.id,
				fields,
				reason
			}
		})
	}
	const prepareCashbookByDescription = (cashbooksData) => {
		const res = cashbooksData?.reduce((acc, curr) => {
			const currToSet = [{
				name: curr.description,
				amount: (curr.should || 0) - (curr.have || 0)
			}]
			return [...acc, ...currToSet]
		}, [])
		return res
	}
	return(
		<Fragment>
			<ContentBlock>
				<Button
					text={t('product_section.strings.sum')}
					status={isSum ? 'green' : 'primary'}
					onClick={toggleSum}
				/>
				<DataTable
					columns={total_salesGroup(t)}
					data={isSum ? sumData.salesGroups : data.salesGroups}
					loading={loading}
					// height={400}
					withSearch
					withFooter
					label={t('closing_section.sub_sections.closing')}
				/>
			</ContentBlock>
			<ContentBlock>
				<OldDataTable
					columns={total_currency(mainCurrency, updateData, locale)}
					data={[...(isSum ? sumData.currency : data.currency || [])]}
					withSearch={false}
				/>
			</ContentBlock>
			<ContentBlock>
				<Row>
					<Col xs={6}>
						<DataTable
							columns={total_credit(t)}
							data={[...(isSum ? sumData.paymentMethods : data.paymentMethods || [])] }
							withSearch={false}
						/>
						{((isSum && sumData.cardSchemes?.length > 0) || data.cardSchemes?.length > 0) && <>
							<Divider height={20}/>
							<DataTable
								columns={total_eftbreakdown(t)}
								data={[...(isSum ? sumData.cardSchemes : data.cardSchemes || [])]}
								withSearch={false}
								withFooter
							/>
						</>}
					</Col>
					<Col xs={6}>
						<DataTable
							columns={total_output(t)}
							data={isSum ?
								[...(sumData.coupons || []), ...(prepareCashbookByDescription(sumData.cashbook) || [])] :
								[...(data.coupons || []), ...(prepareCashbookByDescription(data.cashbook) || [])]
							}
							withSearch={false}
						/>
					</Col>
				</Row>
			</ContentBlock>
			<ContentBlock>
				<Row>
					<Col xs={6}>
						<DataTable
							columns={total_statistics(t)}
							data={Object.entries(statistics)}
							withSearch={false}
						/>
					</Col>
					<Col xs={6}>
						<DataTable
							columns={total_specialOps(t)}
							data={isSum ? sumData.specialSales : (data.specialSales || [])}
							withSearch={false}
						/>
						<Divider height={20}/>
						<DataTable
							columns={total_rate(t, locale)}
							data={isSum ? sumData.taxes : (data.taxes || [])}
							withSearch={false}
						/>
					</Col>
				</Row>
			</ContentBlock>
			{data.inactive &&
				<ContentBlock>
					<p>{t('closing_section.strings.closing_is_inactive')}</p>
					<p>{t('closing_section.strings.closing_inactive_date')} {formatDate(data.inactiveDate, 'DD.MM.YYYY HH:mm:ss')}</p>
					<TextEditor
						label={t('closing_section.strings.closing_inactive_reason')}
						defaultText={data.inactiveReason}
						readOnly
						withToolbar={false}
						noForm
					/>
				</ContentBlock>
			}
		</Fragment>
	)
}

export default Total
